import * as d3 from 'd3'
import { CLUSTER_CONFIG } from './constants'
import { gsapAnimationManager } from '../../animation/gsapAnimations'

// 聚類管理類
export class ClusterManager {
  constructor (projection) {
    this.projection = projection
    this.currentExpandedCluster = null // 當前展開的 cluster
    this.isResetting = false
  }

  // 是否允許執行（未在 reset 中）
  _isActive () {
    return !this.isResetting
  }

  // 包裝：reset 中自動早退，避免到處寫 if (this.isResetting)
  _runIfActive (action) {
    const self = this
    return function (...args) {
      if (self.isResetting) return
      return action.apply(this, args)
    }
  }

  // 具 reset 檢查的 setTimeout
  _setTimeoutIfActive (action, delay) {
    return setTimeout(() => {
      if (this.isResetting) return
      action()
    }, delay)
  }

  clusterSites (sites, threshold = CLUSTER_CONFIG.THRESHOLD) {
    const clusters = []
    const visited = new Set()

    sites.forEach((site, i) => {
      if (visited.has(i)) return

      const [x1, y1] = this.projection([site.lon, site.lat])
      const cluster = { sites: [site], cx: x1, cy: y1 }

      for (let j = i + 1; j < sites.length; j++) {
        if (visited.has(j)) continue
        const [x2, y2] = this.projection([sites[j].lon, sites[j].lat])
        const dx = x2 - x1
        const dy = y2 - y1
        const distance = Math.sqrt(dx * dx + dy * dy)
        if (distance < threshold) {
          cluster.sites.push(sites[j])
          visited.add(j)
        }
      }
      clusters.push(cluster)
      visited.add(i)
    })

    // 拆分過大的 cluster
    const finalClusters = []
    clusters.forEach(cluster => {
      if (cluster.sites.length > 10) {
        const subClusters = this.splitLargeCluster(cluster)
        finalClusters.push(...subClusters)
      } else {
        finalClusters.push(cluster)
      }
    })

    return finalClusters
  }

  splitLargeCluster (largeCluster) {
    const maxSitesPerCluster = 10
    const sites = largeCluster.sites
    const subClusters = []

    // 按距離排序，確保相近的站點在同一 sub-cluster
    const sortedSites = [...sites].sort((a, b) => {
      const [ax, ay] = this.projection([a.lon, a.lat])
      const [bx, by] = this.projection([b.lon, b.lat])
      const dx = ax - largeCluster.cx
      const dy = ay - largeCluster.cy
      const distanceA = Math.sqrt(dx * dx + dy * dy)
      const dx2 = bx - largeCluster.cx
      const dy2 = by - largeCluster.cy
      const distanceB = Math.sqrt(dx2 * dx2 + dy2 * dy2)
      return distanceA - distanceB
    })

    // 將站點分組，每組最多 10 個
    for (let i = 0; i < sortedSites.length; i += maxSitesPerCluster) {
      const clusterSites = sortedSites.slice(i, i + maxSitesPerCluster)

      // 計算 sub-cluster 的中心位置
      const totalX = clusterSites.reduce((sum, site) => {
        const [x] = this.projection([site.lon, site.lat])
        return sum + x
      }, 0)
      const totalY = clusterSites.reduce((sum, site) => {
        const [, y] = this.projection([site.lon, site.lat])
        return sum + y
      }, 0)

      let cx = totalX / clusterSites.length
      let cy = totalY / clusterSites.length

      // 調整位置避免與其他 sub-cluster 重疊
      const minDistance = 8
      let attempts = 0
      const maxAttempts = 10

      while (attempts < maxAttempts) {
        let hasOverlap = false

        for (const existingCluster of subClusters) {
          const dx = cx - existingCluster.cx
          const dy = cy - existingCluster.cy
          const distance = Math.sqrt(dx * dx + dy * dy)

          if (distance < minDistance) {
            hasOverlap = true
            const angle = Math.atan2(dy, dx)
            const offsetX = Math.cos(angle) * minDistance
            const offsetY = Math.sin(angle) * minDistance

            cx = existingCluster.cx + offsetX
            cy = existingCluster.cy + offsetY
            break
          }
        }

        if (!hasOverlap) break
        attempts++
      }

      subClusters.push({
        sites: clusterSites,
        cx,
        cy
      })
    }

    return subClusters
  }

  drawClusterIcon (cluster, iconLayer, svg, zoom, projection, vm, drawParentIcon, state, iconHandler) {
    if (this.isResetting) return // 外圍鎖
    const { cx, cy } = cluster
    const isAnyExpanded = !!this.currentExpandedCluster

    // 畫 cluster 圓圈
    const clusterCircle = iconLayer.append('circle')
      .attr('cx', cx)
      .attr('cy', cy)
      .attr('r', 0)
      .attr('fill', '#2D303D')
      .attr('stroke', '#373A4B')
      .attr('stroke-width', 0.5)
      .attr('class', 'cluster-circle')
      .style('pointer-events', 'none')
      .style('opacity', isAnyExpanded ? 0.5 : 1)

    // 使用 GSAP 動畫展開圓圈
    gsapAnimationManager.expandClusterCircle(clusterCircle.node(), {
      duration: 0.3,
      radius: 2.5,
      ease: 'power2.out'
    })

    // 畫 cluster 文字
    const clusterText = iconLayer.append('text')
      .attr('x', cx + 0.5)
      .attr('y', cy + 0.8)
      .attr('text-anchor', 'middle')
      .attr('fill', '#fff')
      .attr('class', 'cluster-text')
      .attr('font-size', '2px')
      .style('opacity', isAnyExpanded ? 0.5 : 1)
      .style('pointer-events', 'none')
      .text(cluster.sites.length)

    // 畫透明的點擊區域
    const clusterRect = iconLayer.append('circle')
      .attr('cx', cx)
      .attr('cy', cy)
      .attr('r', 2.5)
      .attr('fill', 'transparent')
      .style('cursor', 'pointer')
      .style('pointer-events', 'all')
      .attr('class', 'cluster-rect')
      .attr('data-cluster-ids', cluster.sites.map(s => s.id).join(','))
      .on('mouseover', () => {})
      .on('mouseout', () => {})

    let isExpanded = false

    // 聚集點擊後 --------------------------------------------------------------
    clusterRect.on('click', this._runIfActive((event) => {
      d3.select(event.currentTarget.parentNode).raise()

      if (isExpanded) {
        this.collapseCurrentCluster(iconLayer, vm, state, svg, zoom, drawParentIcon, iconHandler)
        return
      }

      if (this.currentExpandedCluster && this.currentExpandedCluster !== cluster) {
        this.collapseCurrentCluster(iconLayer, vm, state, svg, zoom, drawParentIcon, iconHandler)
      }

      this.currentExpandedCluster = cluster
      isExpanded = true

      this.bringClusterToFront(clusterCircle, clusterText, clusterRect)
      clusterText.style('display', 'none')
      this.focusOnCluster(cluster, svg, zoom, projection)
      this.removeClusterElements(iconLayer, cx, cy)

      // 隱藏其他圖標
      iconLayer.selectAll('.icon-group').style('opacity', 0.5)
      iconLayer.selectAll('.cluster-circle').style('opacity', 0.5)
      iconLayer.selectAll('.cluster-text').style('opacity', 0.5)
      clusterCircle.style('opacity', 1)

      // 關閉其他群集與圖標的事件，避免先前透明元素攔截
      iconLayer.selectAll('.cluster-rect').style('pointer-events', 'none')
      clusterRect.style('pointer-events', 'all')
      iconLayer.selectAll('.icon-group').style('pointer-events', 'none')

      // 展開當前 cluster 的母icon - 動態網格排列
      const expandedIcons = []
      const iconsPerRow = cluster.sites.length <= 6 ? 3 : 5
      const iconSpacing = cluster.sites.length <= 6 ? 8 : 6
      const rowSpacing = 8

      const avoidOffset = this.calculateAvoidanceOffset(cx, cy, iconLayer)

      cluster.sites.forEach((site, index) => {
        const row = Math.floor(index / iconsPerRow)
        const col = index % iconsPerRow
        const totalCols = Math.min(iconsPerRow, cluster.sites.length - row * iconsPerRow)
        const startX = cx - (totalCols - 1) * iconSpacing / 2
        const x = startX + col * iconSpacing + avoidOffset.x
        const y = cy + (row - Math.floor((cluster.sites.length - 1) / iconsPerRow) / 2) * rowSpacing + avoidOffset.y

        const iconGroup = drawParentIcon(site, true)
        if (iconGroup) {
          iconGroup.attr('transform', `translate(${cx}, ${cy}) scale(0)`)
            .style('pointer-events', 'all')
            .raise()

          const labelGroup = iconGroup.select('.site-label-group')
          if (labelGroup.size()) {
            labelGroup.style('opacity', 0)
          }

          // 添加流水號顯示在圖標下方
          iconGroup.append('text')
            .attr('transform', 'translate(2.5, 6)')
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'middle')
            .attr('fill', '#fff')
            .attr('font-size', '1.2px')
            .attr('class', 'icon-number')
            .attr('data-site-index', index)
            .attr('data-site-id', site.id)
            .text(index + 1)

          const delayTime = cluster.sites.length > 10 ? index * 30 : index * 50
          iconGroup.transition()
            .duration(400)
            .delay(delayTime)
            .ease(d3.easeCubicOut)
            .attr('transform', `translate(${x}, ${y}) scale(1)`)

          expandedIcons.push(iconGroup)
        }
      })

      if (vm && vm.updateSiteNameList) {
        vm.updateSiteNameList(cluster.sites)
      }

      // 確保所有展開的圖標都不顯示名稱標籤，只顯示流水號
      setTimeout(() => {
        cluster.sites.forEach(site => {
          const siteId = site.id || `${site.county}-${site.lat}-${site.lon}`
          const iconGroup = iconLayer.selectAll(`[data-site-id="${siteId}"]`)
          const labelGroup = iconGroup.select('.site-label-group')
          if (labelGroup.size()) {
            labelGroup.style('opacity', 0)
          }
        })
      }, 600)

      this.registerCollapseHandler(cluster, iconLayer, vm, state, svg, zoom, drawParentIcon, iconHandler)
    }))
  }

  // 聚焦到指定 cluster
  // Focus on the specified cluster by zooming and panning the map
  focusOnCluster (cluster, svg, zoom, projection) {
    if (this.isResetting) return // 外圍鎖
    const lons = cluster.sites.map(site => site.lon)
    const lats = cluster.sites.map(site => site.lat)
    const minLon = Math.min(...lons)
    const maxLon = Math.max(...lons)
    const minLat = Math.min(...lats)
    const maxLat = Math.max(...lats)

    const pad = 0.01
    const bounds = [
      [minLon - pad, minLat - pad],
      [maxLon + pad, maxLat + pad]
    ]

    const [[x0, y0], [x1, y1]] = [
      projection(bounds[0]),
      projection(bounds[1])
    ]

    const width = svg.attr('width')
    const height = svg.attr('height')

    const scale = Math.min(8, 1.5 / Math.max((x1 - x0) / width, (y1 - y0) / height))
    const translateX = width / 2 - scale * (x0 + x1) / 2
    const translateY = height / 2 - scale * (y0 + y1) / 2
    const newTransform = d3.zoomIdentity.translate(translateX, translateY).scale(scale)

    svg.transition()
      .duration(300)
      .ease(d3.easeCubicOut)
      .call(zoom.transform, newTransform)
  }

  // 移除指定 cluster 的圓圈、文字和點擊區域
  removeClusterElements (iconLayer, cx, cy) {
    iconLayer.selectAll('.cluster-circle, .cluster-text, .cluster-rect')
      .filter(function () {
        const el = d3.select(this)
        if (el.classed('cluster-circle')) {
          return (+el.attr('cx') === cx) && (+el.attr('cy') === cy)
        }
        if (el.classed('cluster-text')) {
          return (+el.attr('x') === cx) && (+el.attr('y') === cy)
        }
        if (el.classed('cluster-rect')) {
          return (+el.attr('cx') === cx) && (+el.attr('cy') === cy)
        }
        return false
      })
      .remove()
  }

  applyForceSimulation (expandedIcons, iconLayer, iconHandler) {
    if (this.isResetting) return // 外圍鎖
    const obstacles = []

    // 收集其他cluster的位置
    iconLayer.selectAll('.cluster-circle').each(function () {
      const cx = +d3.select(this).attr('cx')
      const cy = +d3.select(this).attr('cy')
      obstacles.push({ x: cx, y: cy, radius: 5 })
    })

    // 收集其他母icon的位置
    iconLayer.selectAll('.icon-group').each(function () {
      const transform = d3.select(this).attr('transform')
      if (transform) {
        const translateMatch = transform.match(/translate\(([^,]+),\s*([^)]+)\)/)
        if (translateMatch) {
          const x = parseFloat(translateMatch[1])
          const y = parseFloat(translateMatch[2])
          const isCurrentIcon = expandedIcons.some(icon => {
            const iconTransform = icon.attr('transform')
            const iconMatch = iconTransform.match(/translate\(([^,]+),\s*([^)]+)\)/)
            return iconMatch &&
            Math.abs(parseFloat(iconMatch[1]) - x) < 0.1 &&
            Math.abs(parseFloat(iconMatch[2]) - y) < 0.1
          })
          if (!isCurrentIcon) {
            const circle = d3.select(this).select('.progress-bg')
            const actualRadius = circle.size() ? parseFloat(circle.attr('r')) : 1.5
            obstacles.push({ x, y, radius: Math.max(4, actualRadius * 2) })
          }
        }
      }
    })

    const expandedIconData = expandedIcons.map((iconGroup, index) => {
      const transform = iconGroup.attr('transform')
      const translateMatch = transform.match(/translate\(([^,]+),\s*([^)]+)\)/)

      const circle = iconGroup.select('.progress-bg')
      const actualRadius = circle.size() ? parseFloat(circle.attr('r')) : 1.5

      return {
        iconGroup,
        x: translateMatch ? parseFloat(translateMatch[1]) : 0,
        y: translateMatch ? parseFloat(translateMatch[2]) : 0,
        originalX: translateMatch ? parseFloat(translateMatch[1]) : 0,
        originalY: translateMatch ? parseFloat(translateMatch[2]) : 0,
        radius: actualRadius,
        index
      }
    })

    // 障礙物力場，用於防止圖標與障礙物重疊
    const obstacleForce = () => {
      expandedIconData.forEach(d => {
        obstacles.forEach(obstacle => {
          const dx = d.x - obstacle.x
          const dy = d.y - obstacle.y
          const distance = Math.sqrt(dx * dx + dy * dy)
          const minDistance = 8

          if (distance < minDistance && distance > 0) {
            const force = (minDistance - distance) / distance
            d.x += dx * force * 0.5
            d.y += dy * force * 0.5
          }
        })
      })
    }

    // 排斥力，用于防止圖標間的重疊
    const repelForce = () => {
      expandedIconData.forEach((d, i) => {
        expandedIconData.forEach((other, j) => {
          if (i !== j) {
            const dx = d.x - other.x
            const dy = d.y - other.y
            const distance = Math.sqrt(dx * dx + dy * dy)
            const minDistance = Math.max(5, (d.radius + other.radius) * 2)

            if (distance < minDistance && distance > 0) {
              const force = (minDistance - distance) / distance
              d.x += dx * force * 0.3
              d.y += dy * force * 0.3
            }
          }
        })
      })
    }

    const expandedSimulation = d3.forceSimulation(expandedIconData)
      .force('collide', d3.forceCollide(d => Math.max(4, d.radius * 1.5)))
      .force('x', d3.forceX(d => d.originalX).strength(0.2))
      .force('y', d3.forceY(d => d.originalY).strength(0.2))
      .force('obstacle', obstacleForce)
      .force('repel', repelForce)
      .on('tick', () => {
        expandedIconData.forEach(d => {
          d.iconGroup.attr('transform', `translate(${d.x}, ${d.y})`)
        })
      })
      .stop()

    expandedSimulation.alpha(1).restart()
    const simulationTime = expandedIconData.length > 10 ? 2500 : 2000
    setTimeout(() => expandedSimulation.stop(), simulationTime)
  }

  bringClusterToFront (clusterCircle, clusterText, clusterRect) {
    clusterCircle.raise()
    clusterText.raise()
    clusterRect.raise()
  }

  // 收起當前展開的叢集 --------------------------------------------------------------
  collapseCurrentCluster (iconLayer, vm, state, svg, zoom, drawParentIcon, iconHandler) {
    if (!this.currentExpandedCluster) return

    const cluster = this.currentExpandedCluster

    iconLayer.selectAll('.icon-group').style('opacity', 1)
    iconLayer.selectAll('.cluster-circle').style('opacity', 1)
    iconLayer.selectAll('.cluster-text').style('opacity', 1)

    if (vm && vm.clearSiteNameList) {
      vm.clearSiteNameList()
    }

    cluster.sites.forEach(site => {
      const siteId = site.id || `${site.county}-${site.lat}-${site.lon}`
      const iconGroup = iconLayer.selectAll(`[data-site-id="${siteId}"]`)
      const labelGroup = iconGroup.select('.site-label-group')
      if (labelGroup.size()) {
        labelGroup.style('opacity', 0)
      }
    })

    if (iconHandler && iconHandler.clearLabelOverrides) {
      iconHandler.clearLabelOverrides(cluster.sites)
    }

    cluster.sites.forEach(site => {
      if (iconHandler && iconHandler.collapseChildIcons) {
        iconHandler.collapseChildIcons(site)
      }
    })

    cluster.sites.forEach(site => {
      const siteId = site.id || `${site.county}-${site.lat}-${site.lon}`
      const iconGroup = iconLayer.selectAll(`[data-site-id="${siteId}"]`)

      if (iconGroup.size()) {
        iconGroup.style('pointer-events', 'none')
          .transition('.icon-group', 'parent-icon', '.icon-number')
          .duration(120)
          .ease(d3.easeCubicOut)
          .style('opacity', 0)
          .attr('transform', `translate(${cluster.cx}, ${cluster.cy}) scale(0)`)
          .on('end', function () {
            d3.select(this).remove()
          })
      }
    })

    this._setTimeoutIfActive(() => {
      this.drawClusterIcon(cluster, iconLayer, svg, zoom, this.projection, vm, drawParentIcon, state, iconHandler)
    }, 300)

    if (!this.currentExpandedCluster) {
      iconLayer.selectAll('.cluster-circle').transition()
        .duration(300)
        .style('opacity', 1)
      iconLayer.selectAll('.cluster-text').transition()
        .duration(300)
        .style('opacity', 1)
    }

    state.hasChildIcons = false
    this.currentExpandedCluster = null

    // 收合後恢復其他元素的事件
    iconLayer.selectAll('.cluster-rect').style('pointer-events', 'all')
    iconLayer.selectAll('.icon-group').style('pointer-events', 'all')
  }

  calculateAvoidanceOffset (cx, cy, iconLayer) {
    const minDistance = 12
    const offset = { x: 0, y: 0 }

    const otherClusters = []
    iconLayer.selectAll('.cluster-circle').each(function () {
      const circle = d3.select(this)
      const otherCx = +circle.attr('cx')
      const otherCy = +circle.attr('cy')
      if (otherCx !== cx || otherCy !== cy) {
        otherClusters.push({ cx: otherCx, cy: otherCy })
      }
    })

    for (const otherCluster of otherClusters) {
      const dx = cx - otherCluster.cx
      const dy = cy - otherCluster.cy
      const distance = Math.sqrt(dx * dx + dy * dy)

      if (distance < minDistance) {
        const angle = Math.atan2(dy, dx)
        const offsetDistance = minDistance - distance + 2

        offset.x += Math.cos(angle) * offsetDistance
        offset.y += Math.sin(angle) * offsetDistance
      }
    }

    return offset
  }

  registerCollapseHandler (cluster, iconLayer, vm, state, svg, zoom, drawParentIcon, iconHandler) {
    if (this.isResetting) return // 外圍鎖

    const wrappedHandler = this._runIfActive((e) => {
      const isParentIcon = e.target && e.target.classList && (
        e.target.classList.contains('progress-bg') ||
        e.target.classList.contains('progress-ring') ||
        e.target.classList.contains('image-hover-shadow')
      )
      if (isParentIcon) return

      const isChildIcon = e.target && e.target.classList && (
        e.target.classList.contains('child-icon')
      )
      if (isChildIcon) return

      const isClusterCircle = e.target && e.target.classList && (
        e.target.classList.contains('cluster-circle') ||
        e.target.classList.contains('cluster-text') ||
        e.target.classList.contains('cluster-rect')
      )
      if (isClusterCircle) return

      const infoWindow = document.getElementById('infoWindow')
      if (infoWindow && infoWindow.contains(e.target)) return

      // 檢查是否點擊在模態視窗內
      const modalBk = document.querySelector('.modal-bk')
      const modalMain = document.querySelector('.modal')
      const isClickInsideModal =
        (modalBk && modalBk.contains(e.target)) ||
        (modalMain && modalMain.contains(e.target))
      if (isClickInsideModal) return

      if (infoWindow && infoWindow.style.display === 'block') {
        const { fadeOutDownElement } = require('@/views/situationRoom/animation/zoomAndFade')
        fadeOutDownElement(infoWindow, 0.2, () => {
          infoWindow.style.display = 'none'
        })
      }

      this.collapseCurrentCluster(iconLayer, vm, state, svg, zoom, drawParentIcon, iconHandler)

      document.removeEventListener('click', wrappedHandler, true)
    })

    setTimeout(() => {
      document.addEventListener('click', wrappedHandler, true)
    }, 0)
  }
}
